import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — you can log in now");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-5 py-10">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="h-9 w-9 rounded-lg bg-forest flex items-center justify-center"><Home className="h-5 w-5 text-white" /></div>
        <span className="font-heading font-semibold text-lg text-ink">LocalLease</span>
      </Link>
      <div className="w-full max-w-md bg-white border border-line rounded-2xl shadow-sm p-8">
        {done ? (
          <div className="text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
            <h1 className="mt-4 text-2xl font-heading font-semibold text-ink">Password updated</h1>
            <button data-testid="reset-goto-login" onClick={() => navigate("/login")} className="mt-6 bg-forest text-white px-6 py-2.5 rounded-lg font-medium hover:bg-forest-hover">Go to login</button>
          </div>
        ) : !token ? (
          <div className="text-center text-mute">Invalid reset link.</div>
        ) : (
          <>
            <h1 className="text-2xl font-heading font-semibold text-ink">Set a new password</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div><Label>New password</Label><Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 border-line" /></div>
              <div><Label>Confirm password</Label><Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 border-line" /></div>
              {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
              <button data-testid="reset-submit" disabled={loading} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover flex items-center justify-center gap-2 disabled:opacity-60">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Update password</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
